import React from "react";
import WarningUpgrade from "/public/icon/warning-upgrade.svg";
import { useUserAccount } from "@/states/auth/selectoers";
import { Account } from "@/apis/clients";
import {
  UPGRADE_ALERT_COPY,
  UPGRADE_ALERT_BUSINESS_COPY,
  UpgradeAlertReason,
} from "@/utils/upgrade-alert";

type ErrorLimitByPlanModalProps = {
  onClose: () => void;
  currentAccount?: Account;
  /** 理由別文言のキー。未指定時は従来の共通文言を表示する */
  reason?: UpgradeAlertReason;
};

const isBusinessReason = (
  reason: UpgradeAlertReason | undefined,
): reason is keyof typeof UPGRADE_ALERT_BUSINESS_COPY =>
  reason !== undefined && reason in UPGRADE_ALERT_BUSINESS_COPY;

const ErrorLimitByPlanModal: React.FC<ErrorLimitByPlanModalProps> = ({
  onClose,
  currentAccount,
  reason,
}) => {
  const account = useUserAccount() || currentAccount;
  const isBusinessPlan = Boolean(account?.user_plan_config?.is_business_plan);

  const texts = [
    {
      title: "現在ご利用中プランの上限数に達しました。",
      content:
        "プランをアップグレードすると、ページ数の増加、お得な決済手数料、オプション機能のご利用が可能になります。",
    },
    {
      title: "ご利用中のビジネスプランの上限数に達しました。",
      content:
        "現在利用していないコンテンツ・ページ・アカウント等を削除の上、再度お試しください。プランの詳細については",
    },
  ];

  const businessReasonCopy =
    isBusinessPlan && isBusinessReason(reason)
      ? UPGRADE_ALERT_BUSINESS_COPY[reason]
      : undefined;
  const reasonCopy =
    !isBusinessPlan && reason ? UPGRADE_ALERT_COPY[reason] : undefined;

  const title =
    businessReasonCopy?.title ?? reasonCopy?.title ?? texts[Number(isBusinessPlan)]?.title;
  const content =
    businessReasonCopy?.description ??
    reasonCopy?.description ??
    texts[Number(isBusinessPlan)]?.content;

  // ビジネスプランまたは理由別文言にリンクがある場合は、プラン詳細ページ等へ誘導する
  const link = businessReasonCopy
    ? businessReasonCopy.linkUrl
      ? { text: businessReasonCopy.linkText ?? "こちら", url: businessReasonCopy.linkUrl }
      : undefined
    : reasonCopy?.linkUrl
      ? { text: reasonCopy.linkText ?? "こちら", url: reasonCopy.linkUrl }
      : isBusinessPlan
        ? { text: "こちら", url: "/user/accounts/plan?tab=2" }
        : undefined;

  const handleOpenLink = (url: string) => {
    void window.open(url, "_blank");
  };

  return (
    <div
      className="fixed inset-0 bg-gray-800 bg-opacity-50 flex items-center justify-center z-[500] !mt-0"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-[4px] w-[680px] min-h-[308px] p-[32px] px-[24px] shadow-lg text-center flex flex-col items-center justify-between gap-[24px]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex flex-col items-center w-[430px] gap-[16px] p-[12px]">
          <div className="text-orange-500">
            <WarningUpgrade />
          </div>
          <p className="font-noto text-center font-bold text-[16px] leading-[24px]">
            {title}
          </p>
          <p className="font-noto text-left text-[12px] leading-[20px]">
            {content}
            {link && (
              <span
                onClick={() => handleOpenLink(link.url)}
                className="text-[#3ABCA8] cursor-pointer"
              >
                {link.text}
              </span>
            )}
          </p>
        </div>

        <div className="flex justify-around gap-[12px]">
          <button
            className="w-[180px] h-[44px] rounded-[4px] border border-[#3ABCA8] text-[#3ABCA8] font-noto text-center font-bold text-[16px] leading-[24px]"
            onClick={onClose}
          >
            閉じる
          </button>
          {!isBusinessPlan && (
            <button
              className="w-[180px] h-[44px] rounded-[4px] bg-[#3ABCA8] text-white font-noto text-center font-bold text-[16px] leading-[24px]"
              onClick={() => handleOpenLink("/user/accounts/plan?tab=2")}
            >
              アップグレードする
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ErrorLimitByPlanModal;
